import React, { useState, useRef, useEffect } from "react";
import { IconSearch, IconX } from "@tabler/icons-react";
import { useLanguage } from "../../context/LanguageContext";

type Props = {
  name?: string;
  className?: string;
  inputClassName?: string;
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onClear?: () => void;
  placeholder?: string;
  defaultOpen?: boolean;
};

export default function PremiumSearch({
  name,
  className = "",
  inputClassName = "",
  value = "",
  onChange,
  onClear,
  placeholder,
  defaultOpen = false,
}: Props) {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState<boolean>(Boolean(value) || defaultOpen);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value) {
      setIsOpen(true);
    }
  }, [value]);

  // Keyboard shortcut: Press ESC to clear search & collapse
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, value, onChange, onClear]);

  const handleOpen = () => {
    if (!isOpen) {
      setIsOpen(true);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 200);
    }
  };

  const handleClose = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (onChange) {
      const fakeEvent = {
        target: { value: "" },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(fakeEvent);
    }
    onClear?.();
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const displayText = placeholder || (name ? t(name) : t("Search anything..."));

  return (
    <div className={`relative inline-flex flex-col items-center group ${className}`}>
      {/* ── PREMIUM MORPHING SEARCH CONTAINER ── */}
      <div
        onClick={handleOpen}
        className={`
          relative flex items-center bg-white cursor-pointer select-none
          transition-all duration-750 ease-[cubic-bezier(0.16,1,0.3,1)] overflow-hidden z-10
          ${
            isOpen
              ? "w-[260px] sm:w-[300px] h-8 px-2.5 border-2 border-[#FBD037] rounded-md shadow-[0_6px_20px_rgba(251,208,55,0.15)]"
              : "w-8 h-8 px-0 border border-slate-200 hover:border-[#FBD037] rounded-md justify-center shadow-xs hover:shadow-md transition-all duration-200"
          }
        `}
      >
        {/* Search Icon */}
        <IconSearch
          size={17}
          stroke={2.5}
          className="shrink-0 text-slate-700 transition-colors"
        />

        {/* Search Input (Shown when expanded) */}
        {isOpen && (
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={onChange}
            placeholder={displayText}
            className={`flex-1 ml-2 bg-transparent text-xs font-semibold text-[#131327] outline-none placeholder:text-gray-400 placeholder:font-normal ${inputClassName}`}
          />
        )}

        {/* Close Button (×) */}
        {isOpen && (
          <button
            type="button"
            onClick={handleClose}
            className="ml-1 p-0.5 rounded-full hover:bg-yellow-100/80 text-[#212121] hover:text-red-500 transition-colors cursor-pointer shrink-0"
          >
            <IconX size={16} stroke={2.5} />
          </button>
        )}
      </div>
    </div>
  );
}
